import { useState, useRef, useCallback, useEffect } from "react";
import { Award, CheckCircle, X, ChevronDown } from "lucide-react";
import { AnimatedBlock } from "../shared/AnimatedBlock";
import { ContactModal } from "../Contact/ContactModal";
import { ARROW_COLOR_MAP } from "../../data/courses";

function CourseCard({ card, idx, enrollBtn, lang, overviewLabel, learnLabel, outcomeLabel }) {
  const [open, setOpen] = useState(false);
  const cardRef = useRef(null);
  const tooltipRef = useRef(null);
  const { title, icon: Icon, desc, bg, duration, level, cert, detail } = card;

  const openTooltip = useCallback(() => {
    setOpen(true);
    document.body.style.overflow = "hidden";
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        tooltipRef.current && !tooltipRef.current.contains(e.target) &&
        cardRef.current && !cardRef.current.contains(e.target)
      ) { setOpen(false); document.body.style.overflow = ""; }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") { setOpen(false); document.body.style.overflow = ""; } };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  // Extract a solid hex from bg class for the triangle arrow colour
  const accentColor = ARROW_COLOR_MAP[bg] || "#1e3a8a";

  return (
    <>
      <AnimatedBlock
        delay={(idx % 4) * 100}
        className="relative bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group cursor-pointer select-none"
      >
        {/* Red corner triangle — Excel comment indicator */}
        <div
          className="absolute top-0 right-0 w-0 h-0"
          style={{
            borderStyle: "solid",
            borderWidth: "0 14px 14px 0",
            borderColor: `transparent ${accentColor} transparent transparent`,
            borderRadius: "0 8px 0 0",
          }}
        />

        <div
          ref={cardRef}
          onClick={openTooltip}
          className="flex flex-col h-full"
        >
          <div className={`w-12 h-12 rounded-full ${bg} text-white flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110`}>
            <Icon className="w-6 h-6" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2.5 py-1">
              <Award className="w-3 h-3" />{cert}
            </span>
          </div>
        </div>
      </AnimatedBlock>

      {/* ── Full Modal ── */}
      {open && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(0,0,0,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "16px",
            animation: "modalFadeIn 0.2s ease",
          }}
          onClick={(e) => { if (e.target === e.currentTarget) { setOpen(false); document.body.style.overflow = ""; } }}
        >
          <div
            ref={tooltipRef}
            style={{
              width: "100%", maxWidth: 680,
              maxHeight: "90vh",
              display: "flex", flexDirection: "column",
              borderRadius: 20,
              overflow: "hidden",
              boxShadow: "0 25px 60px rgba(0,0,0,0.35)",
              border: `3px solid ${accentColor}`,
              background: "#fff",
              animation: "modalSlideUp 0.25s cubic-bezier(0.22,1,0.36,1)",
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-6 py-4 flex-shrink-0"
              style={{ background: accentColor }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-white text-xl font-black leading-tight">{title}</h2>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-white/80 text-xs bg-white/15 rounded-full px-2.5 py-0.5">
                      <Award className="w-3 h-3" />{cert}
                    </span>
                    <span className="inline-flex items-center gap-1 text-white/80 text-xs bg-white/15 rounded-full px-2.5 py-0.5">
                      {level}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => { setOpen(false); document.body.style.overflow = ""; }}
                className="text-white/70 hover:text-white transition-colors ml-4 flex-shrink-0 w-8 h-8 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5" style={{ background: "#fff" }}>

              {/* Overview */}
              <div>
                <p className="font-black text-gray-400 uppercase tracking-widest text-xs mb-2">
                  {overviewLabel || (lang === "az" ? "Kurs haqqında" : lang === "ru" ? "О курсе" : "Overview")}
                </p>
                <p className="text-gray-700 text-base leading-relaxed">{detail.overview}</p>
              </div>

              {/* Topics */}
              <div>
                <p className="font-black text-gray-400 uppercase tracking-widest text-xs mb-3">
                  {learnLabel || (lang === "az" ? "Nə öyrənəcəksiniz" : lang === "ru" ? "Чему вы научитесь" : "What you'll learn")}
                </p>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {detail.topics.map((topic, i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-700 text-sm leading-relaxed">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{topic}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Outcome */}
              <div
                className="rounded-xl p-4"
                style={{ background: accentColor + "12", border: `1.5px solid ${accentColor}40` }}
              >
                <p className="font-black text-gray-400 uppercase tracking-widest text-xs mb-2">
                  {outcomeLabel || (lang === "az" ? "Karyera nəticəsi" : lang === "ru" ? "Карьерный результат" : "Career outcome")}
                </p>
                <p className="text-gray-800 text-base leading-relaxed font-medium">{detail.outcome}</p>
              </div>

              {/* Müraciət et CTA */}
              <ContactModal accentColor={accentColor} title={title} lang={lang} enrollBtn={enrollBtn} onCloseParent={() => { setOpen(false); document.body.style.overflow = ""; }} />
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes commentPop {
          from { opacity: 0; transform: scale(0.88) translateY(-6px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes modalFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes modalSlideUp {
          from { opacity: 0; transform: translateY(30px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const scrollTo = (id, closeMenu) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  if (closeMenu) closeMenu(false);
};

// ─── Mobile Course List ───────────────────────────────────────────────────────

function MobileCourseList({ cards, enrollBtn, lang, overviewLabel, learnLabel, outcomeLabel }) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const visibleCards = isMobile && !showAll ? cards.slice(0, 5) : cards;

  const moreLabel = lang === "az" ? "Daha çox kurs gör" : lang === "ru" ? "Показать все курсы" : "Show more courses";
  const lessLabel = lang === "az" ? "Daha az göstər" : lang === "ru" ? "Свернуть" : "Show less";

  return (
    <div className="mb-10">
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {visibleCards.map((card, idx) => (
          <CourseCard key={card.title} card={card} idx={idx} enrollBtn={enrollBtn} lang={lang} overviewLabel={overviewLabel} learnLabel={learnLabel} outcomeLabel={outcomeLabel} />
        ))}
      </div>
      {isMobile && (
        <div className="flex justify-center mt-8">
          <button
            onClick={() => setShowAll(v => !v)}
            className="inline-flex items-center gap-2 bg-blue-900 text-white px-8 py-4 rounded-full font-semibold text-base hover:bg-blue-800 transition-all shadow-lg active:scale-95"
          >
            {showAll ? lessLabel : moreLabel}
            <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${showAll ? "rotate-180" : ""}`} />
          </button>
        </div>
      )}
    </div>
  );
}

export { CourseCard, MobileCourseList };
